'use client';

import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { SCAN_JUMP_CTA_LABEL } from '@/lib/conversion-copy';
import { HalcyLogo } from '../HalcyLogo';
import { useBookingModal } from '../modals/BookingModalContext';
import { landingPageEvents } from '../PostHogProvider';
import styles from './HomepageHeader.module.css';

/**
 * The homepage top bar, built to the geometry measured off functionhealth.com on
 * 2026-09-12 and approved as cc-12 (`.claude/design-specs/2026-09-12-halcy-homepage-function-bar.html`).
 *
 * Paint contract, one rule: the bar is the solid page-coloured band ONLY when the
 * page colour is the whole backdrop. Over anything else - a photograph, the hero's
 * sage band, a midnight section, the footer - it is the inset glass bar. Those
 * bands declare themselves with `data-header-zone="dark" | "tint"`; a section gets
 * it automatically from `<Section tone="dark">`.
 *
 * The test is whether a zone overlaps the strip from the top of the viewport down
 * to the bar's floor, which is what produces the two thresholds Function has: the
 * bar returns to glass the moment a dark band TOUCHES it, and stays glass until
 * that band has left the top of the screen. One threshold in each direction would
 * either flash white over a dark section or hold glass far too long.
 *
 * `data-tone` then carries the backdrop so the ink can follow it: light over the
 * dark bands, ordinary ink behind a whitened frost over the sage. White-on-sage is
 * the one place Function's own bar is genuinely hard to read, and there is no
 * reason to copy that.
 *
 * Nothing here samples a colour, and nothing should: the film's brightness changes
 * every frame, so a bar that followed it would chase the film instead of the page.
 * Zones are the abstraction. What the film's brightness DOES reach is the glass
 * itself, which is transparent by design - the stabiliser for that is the scrim in
 * the stylesheet, not a reading taken here.
 */

/* `/for` is the permanent industry hub. It is labelled "Healthcare" until the
   first non-healthcare industry page ships, then "Industries"; the URL never
   changes (HAL-1915). Learn lives in the footers, not here. */
const NAV_LINKS = [
  { href: '/#how-it-works', label: 'How it works' },
  { href: '/case-studies', label: 'Case studies' },
  { href: '/research', label: 'Research' },
  { href: '/for', label: 'Healthcare' },
  { href: '/about', label: 'About' },
] as const;

/** The bar's inset when it is glass. Also the constant the floor is measured from. */
const LAYER_INSET = 16;

/** What the bar is painted over. Exported for `SiteHeader`'s `initialTone`. */
export type HeaderTone = 'page' | 'dark' | 'tint';

export function HomepageHeader({
  initialTone = 'dark',
}: {
  initialTone?: HeaderTone;
}): React.ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const [tone, setTone] = useState<HeaderTone>(initialTone);
  const headerRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const { openModal } = useBookingModal();

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const handleTalkToUs = useCallback(
    (entryPoint: 'header' | 'mobile-menu') => {
      landingPageEvents.ctaClicked('talk-to-us', entryPoint);
      closeMenu();
      openModal({ placement: entryPoint });
    },
    [closeMenu, openModal]
  );

  /* Paint. Cheap enough to run on every scroll frame: a handful of rects, no writes
     unless the tone actually changed. */
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const paint = (): void => {
      const navHeight = parseFloat(getComputedStyle(header).getPropertyValue('--nav-h')) || 80;
      const floor = LAYER_INSET + navHeight;
      const middle = LAYER_INSET + navHeight / 2;
      const zones = document.querySelectorAll<HTMLElement>('[data-header-zone]');

      let widest: HeaderTone | null = null;
      let most = 0;
      let onMiddle: HeaderTone | null = null;

      zones.forEach((zone) => {
        const rect = zone.getBoundingClientRect();
        const overlap = Math.min(rect.bottom, floor) - Math.max(rect.top, 0);
        const zoneTone = zone.dataset.headerZone === 'tint' ? 'tint' : 'dark';
        if (overlap > most) {
          most = overlap;
          widest = zoneTone;
        }
        if (rect.top <= middle && rect.bottom > middle) onMiddle = zoneTone;
      });

      setTone(widest === null ? 'page' : (onMiddle ?? widest));
    };

    paint();
    window.addEventListener('scroll', paint, { passive: true });
    window.addEventListener('resize', paint);
    return () => {
      window.removeEventListener('scroll', paint);
      window.removeEventListener('resize', paint);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        closeMenu();
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeMenu, menuOpen]);

  /* Focus trap. The open panel covers the page, so Tab must not walk out of it into
     content the reader cannot see. The cycle spans the whole header rather than the
     panel alone, because the bar's own controls - the brand, the scan button and the
     hamburger that closes this - stay visible above it. The nav links and "Talk to
     us" are `display: none` below 1280, which is the only width the panel opens at,
     so they drop out of the cycle on their own.

     Restoring focus on close is what makes the trap usable with a keyboard, and the
     one case where it must NOT win is the booking modal: `handleTalkToUs` closes the
     menu and opens the modal in the same tick, and the modal's own effect runs after
     this cleanup, so the modal's input keeps the focus. */
  useEffect(() => {
    const header = headerRef.current;
    const panel = panelRef.current;
    const toggle = menuButtonRef.current;
    if (!menuOpen || !header || !panel) return;

    previousActiveElement.current = document.activeElement as HTMLElement | null;

    /* `offsetParent` is null for an element inside a `display: none` subtree, which
       is how the desktop nav and "Talk to us" drop out below 1280 - a stop on
       either is a Tab that appears to go nowhere. */
    const focusable = (): HTMLElement[] =>
      Array.from(header.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')).filter(
        (element) => element.offsetParent !== null
      );

    panel.querySelector<HTMLElement>('a[href], button:not([disabled])')?.focus();

    const handleTabKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;
      const elements = focusable();
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => {
      document.removeEventListener('keydown', handleTabKey);
      /* Safari does not focus a button on click, so a mouse-opened menu stores
         `body` here. Restoring to it would drop focus out of the page on close;
         the toggle is the right destination in that case, and it is where the
         Escape handler already sends it. */
      const previous = previousActiveElement.current;
      if (previous && previous !== document.body) previous.focus();
      else toggle?.focus();
    };
  }, [menuOpen]);

  useEffect(() => {
    // A hidden compact menu must not leave desktop scrolling locked.
    const handleResize = (): void => {
      if (window.innerWidth >= 1280) closeMenu();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [closeMenu]);

  /* An open menu always sits on the solid band, whatever is behind it. */
  const paintedTone: HeaderTone = menuOpen ? 'page' : tone;

  return (
    <>
      <header
        ref={headerRef}
        className={clsx(styles.header, menuOpen && styles.menuOpen)}
        data-homepage-header
        data-tone={paintedTone}
        data-solid={paintedTone === 'page' ? 'true' : undefined}
      >
        <div className={styles.inner}>
          <Link href="/" aria-label="Halcy home" className={styles.brand} onClick={closeMenu}>
            <HalcyLogo
              className={styles.lockup}
              iconClassName={styles.mark}
              textClassName={styles.wordmark}
            />
          </Link>

          <nav className={styles.nav} aria-label="Primary navigation">
            {NAV_LINKS.map((link) => (
              <Link key={link.href} href={link.href}>
                {link.label}
              </Link>
            ))}
          </nav>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.talkButton}
              aria-haspopup="dialog"
              onClick={() => handleTalkToUs('header')}
            >
              Talk to us
            </button>
            <a
              className={styles.scanButton}
              href="/#free-scan"
              onClick={() => {
                landingPageEvents.ctaClicked('free-scan', 'header');
                closeMenu();
              }}
            >
              {SCAN_JUMP_CTA_LABEL}
            </a>
            <button
              ref={menuButtonRef}
              type="button"
              className={styles.menuButton}
              aria-expanded={menuOpen}
              aria-controls="homepage-menu"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <line className={styles.lineTop} x1="3" y1="12" x2="21" y2="12" />
                <line className={styles.lineMid} x1="3" y1="12" x2="21" y2="12" />
                <line className={styles.lineBot} x1="3" y1="12" x2="21" y2="12" />
              </svg>
            </button>
          </div>
        </div>

        {/* `inert` covers the 250ms the closing panel is still laid out (the
            stylesheet owns the exit; see `.panel` there). */}
        <div
          ref={panelRef}
          id="homepage-menu"
          className={styles.panel}
          hidden={!menuOpen}
          inert={!menuOpen}
        >
          <nav className={styles.panelPrimary} aria-label="Menu">
            {NAV_LINKS.map((link) => (
              <Link key={link.href} href={link.href} onClick={closeMenu}>
                {link.label}
              </Link>
            ))}
          </nav>

          <div className={styles.panelCta}>
            <button
              type="button"
              aria-haspopup="dialog"
              onClick={() => handleTalkToUs('mobile-menu')}
            >
              Talk to us
            </button>
          </div>
        </div>
      </header>
      {/* Always mounted: a conditionally rendered backdrop is gone before its exit can start. */}
      <div className={styles.backdrop} hidden={!menuOpen} onClick={closeMenu} aria-hidden="true" />
    </>
  );
}
